import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useScenario } from "@/contexts/ScenarioContext";
import { useI18n } from "@/contexts/I18nContext";
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";

const tooltipStyle = {
  contentStyle: { background: "#020617", border: "1px solid #1E293B", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 11 },
  labelStyle: { color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 10 },
};

export default function TrafficChart() {
  const { tenant } = useTenant();
  const { scenario } = useScenario();
  const { t } = useI18n();
  const [data, setData] = useState([]);

  useEffect(() => {
    if (!tenant || !scenario) return;
    api.get(`/dashboard/traffic24h?tenant=${tenant.id}&scenario=${scenario.id}`).then(({ data }) => setData(data));
  }, [tenant, scenario]);

  const peak = Math.max(...data.map((d) => d.inbound + d.outbound), 0);

  return (
    <div className="p-5" data-testid="traffic-chart">
      <div className="grid grid-cols-3 gap-4 mb-4">
        <Stat label={t("traffic.peak_total")} value={peak.toFixed(0)} unit="Mbps" color="#3B82F6" />
        <Stat label={t("traffic.avg_in")} value={data.length ? (data.reduce((a,d)=>a+d.inbound,0)/data.length).toFixed(0) : "—"} unit="Mbps" color="#60A5FA" />
        <Stat label={t("traffic.avg_out")} value={data.length ? (data.reduce((a,d)=>a+d.outbound,0)/data.length).toFixed(0) : "—"} unit="Mbps" color="#34D399" />
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="trIn" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.45} />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="trOut" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
          <XAxis dataKey="hour" stroke="#475569" fontSize={10} interval={3} />
          <YAxis stroke="#475569" fontSize={10} />
          <Tooltip {...tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 10, fontFamily: "IBM Plex Mono", color: "#94A3B8" }} iconSize={8} />
          <Area type="monotone" dataKey="inbound" stroke="#3B82F6" strokeWidth={2} fill="url(#trIn)" name="Inbound" />
          <Area type="monotone" dataKey="outbound" stroke="#10B981" strokeWidth={2} fill="url(#trOut)" name="Outbound" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Stat({ label, value, unit, color }) {
  return (
    <div className="border border-slate-800 bg-[#020617] p-3">
      <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-mono">{label}</div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="font-mono font-bold text-lg" style={{ color }}>{value}</span>
        <span className="text-[10px] font-mono text-slate-500">{unit}</span>
      </div>
    </div>
  );
}
